import React, { useState, useEffect, useCallback, useContext } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart, BarChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  MarkLineComponent,
  MarkPointComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import AppContext from 'context/Context';
import useToken from 'hooks/useToken';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faCalendarAlt, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Spinner, Button } from 'react-bootstrap';
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import AgentFilterSelect from './AgentFilterSelect';

registerLocale('es', es);

echarts.use([
  LineChart,
  BarChart,
  GridComponent,
  TooltipComponent,
  MarkLineComponent,
  MarkPointComponent,
  CanvasRenderer
]);

const formatLocalDateStr = dt => {
  if (!dt) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const ConcurrentTicketsChart = ({
  selectedAgentId = '',
  onSelectAgent,
  agents = [],
  currentUserId,
  currentUserName,
  isLoadingAgents = false
}) => {
  const { token, idUsuario, nombreCompleto } = useToken();
  const { config: { isDark } = {} } = useContext(AppContext);

  const effectiveUserId = currentUserId || idUsuario;
  const effectiveUserName = currentUserName || nombreCompleto;

  const today = new Date();
  const todayStr = formatLocalDateStr(today);
  const [selectedDateObj, setSelectedDateObj] = useState(today);
  const [hourlyData, setHourlyData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const selectedDate = formatLocalDateStr(selectedDateObj);

  const fetchConcurrentData = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      const hasAgent = selectedAgentId && selectedAgentId !== 'todos' && selectedAgentId !== 'all';
      if (hasAgent) {
        params.set('idUsuarioAsignado', selectedAgentId);
      }
      if (selectedDate) {
        params.set('dFecha', selectedDate);
      }

      const resp = await fetch(`/api/SupportEngine/GetDashboardChartConcurrentTickets?${params.toString()}`, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        }
      });

      if (resp.ok) {
        const json = await resp.json();
        const data = json.data || json || [];
        setHourlyData(Array.isArray(data) ? data : []);
      }
    } catch {
      // Silencioso
    } finally {
      setIsLoading(false);
    }
  }, [token, selectedAgentId, selectedDate]);

  useEffect(() => {
    fetchConcurrentData();
  }, [fetchConcurrentData]);

  const now = new Date();
  const isToday = selectedDate === todayStr;
  const currentHour = now.getHours();
  const currentHourKey = isToday ? `${currentHour.toString().padStart(2, '0')}:00` : null;

  // Si es hoy, solo mostramos hasta la hora actual transcurrida
  const maxHour = isToday ? currentHour : 23;

  const categories = [];
  const dataMap = {};
  const agentsByHour = {};
  for (let h = 0; h <= maxHour; h++) {
    const key = `${h.toString().padStart(2, '0')}:00`;
    categories.push(key);
    dataMap[h] = 0;
    agentsByHour[h] = [];
  }

  if (Array.isArray(hourlyData)) {
    hourlyData.forEach(item => {
      const h = item.iHora ?? item.hora;
      const count = item.iMaxTickets ?? item.maxTickets ?? item.iTotal ?? 0;
      if (h !== undefined && h !== null && dataMap[h] !== undefined) {
        dataMap[h] += count;
        if (count > 0 && item.vNombreAsesor) {
          agentsByHour[h].push({
            name: item.vNombreAsesor,
            count
          });
        }
      }
    });
  }

  const chartSeriesData = [];
  let maxConcurrent = 0;

  for (let h = 0; h <= maxHour; h++) {
    const val = dataMap[h] || 0;
    chartSeriesData.push(val);
    if (val > maxConcurrent) maxConcurrent = val;
  }

  const getOption = () => {
    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(15, 23, 42, 0.92)',
        borderColor: '#334155',
        textStyle: { color: '#ffffff', fontSize: 12 },
        formatter: params => {
          if (!params || !params.length) return '';
          const p = params[0];
          const h = parseInt(p.axisValue, 10);
          const agents = agentsByHour[h] || [];
          return `
            <div style="font-weight: 600; margin-bottom: 4px;">Hora: ${p.axisValue}</div>
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background-color:#f59e0b;"></span>
              <span>Pico concurrente: <strong>${p.value} ticket${p.value === 1 ? '' : 's'}</strong></span>
            </div>
            ${agents.length > 0 ? `
              <div style="margin-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.12); padding-top: 5px;">
                ${agents.map(a => `
                  <div style="display: flex; justify-content: space-between; gap: 14px; font-size: 11px; color: #94a3b8; line-height: 1.5;">
                    <span>${a.name}</span>
                    <strong style="color: #f1f5f9;">${a.count}</strong>
                  </div>
                `).join('')}
              </div>
            ` : ''}
          `;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        top: '18%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLine: { lineStyle: { color: isDark ? '#2b3035' : '#e2e8f0' } },
        axisTick: { show: false },
        axisLabel: {
          color: isDark ? '#94a3b8' : '#475569',
          fontSize: 11,
          margin: 12
        }
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        max: Math.max(2, maxConcurrent + 1),
        splitLine: {
          lineStyle: {
            color: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
            type: 'dashed'
          }
        },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: isDark ? '#64748b' : '#94a3b8', fontSize: 11 }
      },
      series: [
        {
          name: 'Pico Concurrente',
          type: 'line',
          smooth: 0.35,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: {
            color: '#f59e0b',
            borderColor: '#ffffff',
            borderWidth: 2
          },
          lineStyle: {
            color: '#f59e0b',
            width: 3,
            shadowColor: 'rgba(245, 158, 11, 0.25)',
            shadowBlur: 8,
            shadowOffsetY: 4
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(245, 158, 11, 0.35)' },
              { offset: 0.8, color: 'rgba(245, 158, 11, 0.05)' },
              { offset: 1, color: 'rgba(245, 158, 11, 0)' }
            ])
          },
          label: {
            show: true,
            position: 'top',
            distance: 6,
            color: '#f59e0b',
            fontWeight: '700',
            fontSize: 11,
            formatter: params => (params.value > 0 ? params.value : '')
          },
          markLine: currentHourKey ? {
            symbol: ['none', 'circle'],
            symbolSize: 6,
            itemStyle: { color: '#64748b' },
            label: { show: false },
            lineStyle: {
              type: 'dashed',
              color: '#cbd5e1',
              width: 1.5
            },
            data: [{ xAxis: currentHourKey }]
          } : undefined,
          data: chartSeriesData
        }
      ]
    };
  };

  return (
    <div className="se-card-panel">
      {/* Encabezado superior con Selector de Día */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              backgroundColor: 'rgba(245, 158, 11, 0.12)',
              color: '#f59e0b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.95rem'
            }}
          >
            <FontAwesomeIcon icon={faLayerGroup} />
          </div>
          <div>
            <h5 className="se-chart-title mb-0" style={{ fontSize: '1.05rem', fontWeight: 600 }}>
              Pico de Tickets Abiertos por Hora 
            </h5>
            <span className="se-chart-subtitle" style={{ fontSize: '0.82rem', color: '#64748b' }}>
              Máximo de tickets activos concurrentes por hora según el día seleccionado
            </span>
          </div>
        </div>

        {/* Controles: Selector de Asesor + Selector de Fecha */}
        <div className="d-flex align-items-center gap-2 flex-wrap w-100 w-sm-auto justify-content-between justify-content-sm-end">
          {onSelectAgent && (
            <div className="d-flex align-items-center">
              <AgentFilterSelect
                selectedAgentId={selectedAgentId}
                onSelectAgent={onSelectAgent}
                agents={agents}
                currentUserId={effectiveUserId}
                currentUserName={effectiveUserName}
                isLoadingAgents={isLoadingAgents}
              />
            </div>
          )}

          <div className="d-flex align-items-center gap-1">
            {/* Selector de fecha con botones previo/siguiente estilo píldora */}
            <div className="d-flex align-items-center bg-light rounded-2 px-1 border" style={{ height: '36px' }}>
              <Button
                variant="link"
                size="sm"
                className="p-1 text-secondary"
                onClick={() => {
                  const prev = new Date(selectedDateObj);
                  prev.setDate(prev.getDate() - 1);
                  setSelectedDateObj(prev);
                }}
                title="Día anterior"
              >
                <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '0.75rem' }} />
              </Button>

              <div className="d-flex align-items-center px-1">
                <FontAwesomeIcon icon={faCalendarAlt} className="text-warning me-1" style={{ fontSize: '0.85rem' }} />
                <DatePicker
                  selected={selectedDateObj}
                  onChange={date => {
                    if (date) setSelectedDateObj(date);
                  }}
                  maxDate={today}
                  locale="es"
                  dateFormat="d 'de' MMMM, yyyy"
                  className="form-control form-control-sm border-0 bg-transparent text-center px-1"
                  style={{ minWidth: '150px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}
                />
              </div>

              <Button
                variant="link"
                size="sm"
                className="p-1 text-secondary"
                onClick={() => {
                  const next = new Date(selectedDateObj);
                  next.setDate(next.getDate() + 1);
                  if (next <= today) {
                    setSelectedDateObj(next);
                  }
                }}
                title="Día siguiente"
                disabled={isToday}
              >
                <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '0.75rem' }} />
              </Button>

              {!isToday && (
                <Button
                  variant="outline-warning"
                  size="sm"
                  className="ms-1 py-0 px-2"
                  style={{ fontSize: '0.72rem', height: '24px', fontWeight: 600 }}
                  onClick={() => setSelectedDateObj(today)}
                >
                  Hoy
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fila de Leyenda superior */}
      <div className="d-flex justify-content-between align-items-center mb-2 px-1 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          {maxConcurrent > 0 && (
            <span
              className="badge"
              style={{
                backgroundColor: 'rgba(245, 158, 11, 0.15)',
                color: '#d97706',
                fontSize: '0.75rem',
                fontWeight: 600,
                padding: '5px 10px',
                borderRadius: '6px'
              }}
            >
              Pico del día: {maxConcurrent} tickets
            </span>
          )}
        </div>

        {isLoading && (
          <div className="d-flex align-items-center gap-1 text-muted" style={{ fontSize: '0.8rem' }}>
            <Spinner animation="border" size="sm" variant="warning" />
            <span>Consultando día...</span>
          </div>
        )}
      </div>

      {/* Caja contenedora con borde */}
      <div
        className="se-chart-inner-box"
        style={{
          border: isDark ? '1px solid #2b3035' : '1px solid #eef2f6',
          borderRadius: '14px',
          backgroundColor: isDark ? '#121e2d' : '#ffffff',
          padding: '10px 14px',
          minHeight: '270px'
        }}
      >
        <div className="se-resolutions-chart-wrapper">
          <ReactEChartsCore
            echarts={echarts}
            option={getOption()}
            notMerge={true}
            lazyUpdate={true}
            style={{ height: '100%', width: '100%' }}
          />
        </div>
      </div>
    </div>
  );
};

export default ConcurrentTicketsChart;
